import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { getTourGroupStyleClass } from 'utils/weekly'
import { Tooltip } from 'components/atoms/Tooltip'
import { IWeeklyOtaReservation } from 'utils/weeklyTypes'

interface IProps {
  tourGroups: Array<{
    assignedGuide: any | null
    tourGroupStatus: 'alert' | 'ng' | 'ok'
    reservedNumber: number
  }>
  unassignedOtaReservations: IWeeklyOtaReservation[]
}

type TourGroupStatusForIcon = 'alert' | 'ng' | 'caution' | 'unassigned'

const TourGroupStatusIcon = ({ status }: { status: TourGroupStatusForIcon }) => {
  const tooltipOptions = React.useMemo(() => {
    switch (status) {
      case 'ng':
        return {
          statusName: 'unassigned-guide',
          content: 'ガイド未アサイン',
        }
      case 'caution':
        return {
          statusName: 'no-guest',
          content: 'ゲストなし',
        }
      case 'alert':
        return {
          statusName: 'sounding-guide',
          content: 'ガイド打診中',
        }
      case 'unassigned':
        return {
          statusName: 'unassigned-guest',
          content: '未アサインゲスト',
        }
      default:
        return null
    }
  }, [status])

  if (tooltipOptions === null) {
    return null
  }

  return (
    <Tooltip content={tooltipOptions.content}>
      <Icon className={tooltipOptions.statusName} aria-hidden="true" />
    </Tooltip>
  )
}

const Icon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  margin-bottom: 4px;
  &::after {
    display: block;
    content: '';
  }
  &.unassigned-guide::after {
    width: 8px;
    height: 8px;
    background-color: ${constants.COLORS.danger};
  }
  &.no-guest::after {
    width: 10px;
    height: 9px;
    background-color: ${constants.COLORS.useally};
    aspect-ratio: 1 / cos(30deg);
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
  }
  &.sounding-guide::after {
    width: 9px;
    height: 9px;
    background-color: ${constants.COLORS.alert};
    border-radius: 50%;
  }
  &.unassigned-guest::after {
    width: 7px;
    height: 7px;
    background-color: ${constants.COLORS.info3};
    transform: rotate(45deg);
  }
`

/**
 * Weekly等で表示されるツアーのツアーグループの丸アイコン
 * @param tourGroups
 * @param unassignedReservations
 * @returns
 */
export const TourGroupIcons = ({ tourGroups, unassignedOtaReservations }: IProps) => {
  const hasUnassignedOtaReservation = unassignedOtaReservations.length > 0

  // ツアーグループのステータスを取得
  // 重複しちゃうので削除してデザインに合わせてソート
  const tourGroupStatusList: TourGroupStatusForIcon[] = tourGroups
    .reduce((prev, tourGroup) => {
      const statusClassName = getTourGroupStyleClass(tourGroup)
      if (
        statusClassName === 'alert' ||
        statusClassName === 'caution' ||
        statusClassName === 'ng'
      ) {
        prev.push(statusClassName)
      }

      return prev
    }, [] as TourGroupStatusForIcon[])
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a, b) => {
      if (a === 'ng') {
        return -1
      }
      if (b === 'ng') {
        return 1
      }

      if (a === 'caution') {
        return -1
      }
      if (b === 'caution') {
        return 1
      }

      if (a === 'alert') {
        return -1
      }
      if (b === 'alert') {
        return 1
      }

      return 0
    })

  // ツアーの未アサイン予約アイコンを追加
  const needShowStatusList: TourGroupStatusForIcon[] = React.useMemo(() => {
    if (hasUnassignedOtaReservation) {
      return [...tourGroupStatusList, 'unassigned']
    }
    return tourGroupStatusList
  }, [hasUnassignedOtaReservation, tourGroupStatusList])

  return (
    <Icons>
      {needShowStatusList.map((iconStatus, index) => {
        return (
          <TourGroupStatusIcon
            key={`tour-group-status-icon-${index}-${iconStatus}`}
            status={iconStatus}
          />
        )
      })}
    </Icons>
  )
}

const Icons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
`
